import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { timeZones, API_CLIENTS_NAME, API_KEY } from '../../utils';

import { clientsSelector, getClientById } from '../../store/slices/clients';
import {
  selectedClientSelector,
  setSelectedClient
} from '../../store/slices/selectedClient';
import { authSelector } from '../../store/slices/auth';

import { getGroups, groupsSelector } from '../../store/slices/groups';
import { postsSelector } from '../../store/slices/posts';
import { unwrapResult } from '@reduxjs/toolkit';
import { useHistory } from 'react-router-dom';

import './Client.scss';
import axios from 'axios';

import UnifiedAboutContent from './UnifiedAboutContent';
import ClientSkeleton from './ClientSkeleton';

// Function to compare two arrays
function comparer(otherArray) {
  return function (current) {
    return (
      otherArray.filter(function (other) {
        return other.groupName === current.groupName;
      }).length === 0
    );
  };
}

function ContentStrategySettings({ match }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const [editClient, setEditClient] = useState(false);
  const [clientDetails, setClientDetails] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editedProps, setEditedProps] = useState(null);
  const [viewSelect, setViewSelect] = useState(1);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectGroupOptions, setSelectGroupOptions] = useState([]);
  const [selectTimeZonesOptions, setSelectTimeZonesOptions] = useState([]);
  const { customerList } = useSelector(clientsSelector);
  const { selectedClient } = useSelector(selectedClientSelector);
  const { loadingPosts } = useSelector(postsSelector);
  const { userInfo } = useSelector(authSelector);
  const { groupsList } = useSelector(groupsSelector);
  const [lastReviews, setLastReviews] = useState([]);
  const [automaticReviews, setAutomaticReviews] = useState(false);

  const standardMergeFields = [
    '~Name',
    '~Address',
    '~City',
    '~Phone',
    '~Website',
    '~BestDish',
    '~BestService',
    '~BestProduct'
  ];
  const btnTagRef = useRef();

  // Styles of search select
  const searchStyle = {
    control: (_, { selectProps: { width } }) => ({
      width: width,
      border: 'none'
    }),
    indicatorsContainer: () => ({
      display: 'none'
    }),
    input: (provided) => ({
      ...provided,
      marginTop: '0.5em'
    })
  };

  const isLoadingRef = useRef(false);
  const currentClientId = useRef(null);

  // Resetear refs cuando cambia el usuario autenticado
  useEffect(() => {
    return () => {
      isLoadingRef.current = false;
      currentClientId.current = null;
      setLoadError(null);
    };
  }, [userInfo.uid]);

  useEffect(() => {
    async function getURLClient() {
      // Si hay un parámetro id en la URL, cargar el cliente por ese id
      if (match.params.id) {
        if (isLoadingRef.current) return;

        isLoadingRef.current = true;

        try {
          const client = await axios.get(
            `${API_CLIENTS_NAME}${match.params.id}`,
            {
              headers: {
                'X-API-KEY': API_KEY,
                Authorization: `bearer ${userInfo.accessToken}`
              }
            }
          );

          if (client.data) {
            if (currentClientId.current !== client.data.id) {
              currentClientId.current = client.data.id;
              dispatch(setSelectedClient(client.data));
            }
          }
        } catch (error) {
          console.error('Error loading client:', error);
        } finally {
          isLoadingRef.current = false;
        }
      } else {
        // Si no hay parámetro id, usar el cliente de Redux (ya cargado por AuthWrapper)
        if (
          selectedClient?.id &&
          currentClientId.current !== selectedClient.id
        ) {
          currentClientId.current = selectedClient.id;
        }
      }
    }

    getURLClient();
    // eslint-disable-next-line
  }, [match.params.id, selectedClient]);

  const isProcessingDetails = useRef(false);

  // Resetear refs y clientDetails cuando cambia el usuario autenticado
  useEffect(() => {
    isProcessingDetails.current = false;
    setClientDetails(null);
  }, [userInfo.uid]);

  const loadClientDetails = useCallback(
    async (force = false) => {
      if (isProcessingDetails.current) return;
      if (!selectedClient?.id) return;
      if (
        !force &&
        currentClientId.current === selectedClient.id &&
        clientDetails
      )
        return;

      isProcessingDetails.current = true;

      try {
        setLoadError(null);

        let res = await dispatch(
          getClientById({ clientId: selectedClient.id })
        );
        res = unwrapResult(res);

        currentClientId.current = selectedClient.id;
        setAutomaticReviews(res.automaticReviews);
        setClientDetails(res);
      } catch (error) {
        setClientDetails(null);
        const message =
          typeof error === 'string'
            ? error
            : error?.payload?.message ||
              error?.message ||
              'No se pudo cargar el cliente.';
        setLoadError(message);
        console.error('Error loading client details:', error);
      } finally {
        isProcessingDetails.current = false;
      }
    },
    [clientDetails, dispatch, selectedClient]
  );

  useEffect(() => {
    if (selectedClient?.id) {
      loadClientDetails();
    }
  }, [selectedClient, loadClientDetails]);

  // Set groups dropdown options
  useEffect(() => {
    if (editedProps) {
      var resultA = groupsList.filter(comparer(editedProps.clientGroups));
      resultA = resultA.map((opt) => ({
        value: opt.id,
        label: opt.groupName
      }));
      setSelectGroupOptions(resultA);
    }
    // eslint-disable-next-line
  }, [editedProps, editClient]);

  // Set timezone dropdown options
  useEffect(() => {
    if (editedProps) {
      let listTimeZone;
      if (clientDetails && clientDetails.timeZone) {
        listTimeZone = timeZones
          .filter((item) => item.name !== clientDetails.timeZone)
          .map((zone) => ({
            value: zone.id,
            label: zone.name
          }));
      } else {
        listTimeZone = timeZones.map((zone) => ({
          value: zone.id,
          label: zone.name
        }));
      }
      setSelectTimeZonesOptions(listTimeZone);
    }
    // eslint-disable-next-line
  }, [editedProps, editClient]);

  return (
    <>
      {clientDetails ? (
        <UnifiedAboutContent
          defaultData={clientDetails}
          callbackFromParent={() => {}}
        />
      ) : loadError ? (
        <div className="client-error-state">
          <h2 className="client-error-state__title">
            No pudimos cargar la información del cliente
          </h2>
          <p className="client-error-state__message">{loadError}</p>
          <button
            type="button"
            className="client-error-state__retry"
            onClick={() => loadClientDetails(true)}>
            Reintentar
          </button>
        </div>
      ) : (
        <ClientSkeleton />
      )}
    </>
  );
}

export default ContentStrategySettings;
